import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  AggregateMetricsRequest,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import type { Condition } from "@/types";

/** Everything that identifies one chart's slice of metric data. */
export interface AggregateQueryInputs {
  metricName: string;
  period: Period;
  aggregation: NumericAggregation;
  /** The resolved window to aggregate over. */
  range: TimeRange;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Session-property / custom-field predicate applied server-side. Part of
   *  the request (and therefore of the React Query key), so tightening the
   *  dashboard's filter refetches rather than redrawing stale rows. */
  condition?: Condition;
  /** Split the response into one series per distinct value of this field —
   *  an entity-prefixed wire path (`device.device_id`,
   *  `session.custom.<name>`). Omit for one series over everything. */
  groupBy?: string;
}

/**
 * The aggregate query a chart is drawn from.
 *
 * Shared between the chart and the card around it, which reads the same
 * response to caption how much data the plot covers: an identical request
 * means an identical React Query key, so the two share one cache entry and
 * one fetch. Built here rather than in either component so they cannot drift
 * into two subtly different keys and two round trips.
 */
export function aggregateRequestFor(
  inputs: AggregateQueryInputs,
): AggregateMetricsRequest {
  return {
    name: inputs.metricName,
    period: inputs.period,
    aggregation: inputs.aggregation,
    time_filter: "end_time",
    start_time: inputs.range.start_ms,
    end_time: inputs.range.end_ms,
    include_device_ids: inputs.deviceIds,
    include_session_ids: inputs.sessionIds,
    condition: inputs.condition,
    group_by: inputs.groupBy,
  };
}
